import { FC, useEffect, useRef } from 'react';
import { TabsEditable } from '@marriott/mi-ui-library';
import { CalendarTabValue, MobileCalendarProps } from './MobileCalendar.types';
import { Calendar } from '../Calendar';
import { StyledMobileCalendar } from './MobileCalendar.styles';
import { CALENDAR_START_DATE, CALENDAR_VARIATION_VERTICAL } from '../../../constants';
import { setCalendarOptions } from '../../../utils';
import { TextFormField } from '../../TextFormField';
import { useCalendarTabState } from '../useCalendarTabState';

const CALENDAR_MODE_BY_TAB: Record<CalendarTabValue, 'single' | 'range'> = {
  [CalendarTabValue.MULTIPLE]: 'range',
  [CalendarTabValue.SINGLE]: 'single',
};

export const MobileCalendar: FC<MobileCalendarProps> = ({
  dates,
  selectedStartDate,
  selectedEndDate,
  isDateRangeFixed = false,
  rangeStartDate,
  rangeEndDate,
  setDefaultDates = false,
  showErrorMessage = false,
  errorMessage,
  isPastDateSelectionDisabled,
  enableFromPastDate,
  onChange,
  calendarTab,
  onCalendarTabChange,
  hideDatesBadge = false,
}) => {
  const {
    label,
    placeholder,
    datePlaceholder,
    night,
    nights,
    shortWeekdayInitials,
    shortWeekdayNames,
    longWeekdayNames,
    shortMonthNames,
    longMonthNames,
    multipleDays,
    singleDay,
  } = dates;

  const calendarRef = useRef<HTMLElement>(null);
  const datesInputRef = useRef<HTMLInputElement>(null);

  const {
    currentActiveTab,
    calendarState,
    startDate,
    endDate,
    displayState,
    tabList,
    setInitialDates,
    updateCurrentActiveTab,
    setDatesForCurrentTab,
  } = useCalendarTabState({
    calendarTab,
    selectedStartDate,
    selectedEndDate,
    night,
    nights,
    multipleDays,
    singleDay,
    onChange,
    onCalendarTabChange,
  });

  useEffect(() => {
    setCalendarOptions({
      shortWeekdayNames,
      longWeekdayNames,
      shortMonthNames,
      longMonthNames,
    });
  }, [shortWeekdayNames, longWeekdayNames, shortMonthNames, longMonthNames]);

  return (
    <StyledMobileCalendar data-testid="groups-MobileCalendar" data-component-name="m-groups-MobileCalendar">
      <section ref={calendarRef} className="date-picker-mobile-container">
        <div className="calendar-sticky-header">
          <TextFormField
            ref={datesInputRef}
            label={label}
            ariaLabel={dates['ariaLabel']}
            placeholder={currentActiveTab === CalendarTabValue.SINGLE ? datePlaceholder : placeholder}
            value={displayState.dateInputValue}
            className="m-input-text-field"
            hideCursor={true}
            showErrorMessage={showErrorMessage}
            errorMessage={errorMessage}
            testId="dates"
            showBadgeContent={!hideDatesBadge && !!displayState.datesLabel}
            badgeContent={!hideDatesBadge ? displayState.datesLabel : ''}
          />
          <section className="groups-tab-container">
            <TabsEditable
              tabList={tabList}
              isSwitcherTabs={true}
              isTablink={true}
              classTabsEnabled="Tabs Enabled"
              isIconTabs={false}
              selectedTab={currentActiveTab}
              setSelectedTab={updateCurrentActiveTab}
            />
          </section>
          {currentActiveTab === CalendarTabValue.SINGLE && dates.message && (
            <section className="calendar-message-container align-items-center">
              <div className="calendar-badge">{dates.message}</div>
            </section>
          )}
        </div>
        <Calendar
          mode={CALENDAR_MODE_BY_TAB[currentActiveTab]}
          variation={CALENDAR_VARIATION_VERTICAL}
          nightLabel={night}
          nightsLabel={nights}
          shortWeekdayInitials={shortWeekdayInitials}
          shortWeekdayNames={shortWeekdayNames}
          longWeekdayNames={longWeekdayNames}
          shortMonthNames={shortMonthNames}
          longMonthNames={longMonthNames}
          setInitialDates={(startDate, endDate, initialStartDate, initialEndDate, selectedDateType) =>
            setInitialDates(currentActiveTab, startDate, endDate, initialStartDate, initialEndDate, selectedDateType)
          }
          setDates={(startDate, endDate, selectedDateType) =>
            setDatesForCurrentTab(startDate, endDate || null, selectedDateType)
          }
          selectedStartDate={startDate}
          selectedEndDate={endDate}
          isDateRangeFixed={currentActiveTab === CalendarTabValue.MULTIPLE ? isDateRangeFixed : false}
          rangeStartDate={currentActiveTab === CalendarTabValue.MULTIPLE ? rangeStartDate : null}
          rangeEndDate={currentActiveTab === CalendarTabValue.MULTIPLE ? rangeEndDate : null}
          setDefaultDates={setDefaultDates}
          isCalendarClosed={false}
          isThereTwoDateInputBox={true}
          isPastDateSelectionDisabled={isPastDateSelectionDisabled}
          enableFromPastDate={enableFromPastDate}
          selectedDateType={calendarState.selectedDateType || CALENDAR_START_DATE}
        />
      </section>
    </StyledMobileCalendar>
  );
};
